import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormLabel, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import DropdownMenu from 'src/components/DropdownMenu';
import theme from 'src/theme';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  CREATE_BURO_FORM,
  createBureauForm
} from 'src/store/actions/bureauFormActions';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const FormOptionsType = [
  { name: 'COMMON.YES', key: true },
  { name: 'COMMON.NO', key: false }
];

const StepFive = ({ data, handleNext, handleBack }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const isLoadingCreateForm = useSelector(
    isLoadingSelector([CREATE_BURO_FORM.action])
  );
  const initialState = useMemo(() => makeInitialState(data), [data]);

  const { t } = useTranslation();

  const onNext = useCallback(
    async values => {
      dispatch(createBureauForm({ ...values }, handleNext));
    },
    [handleNext, data]
  );

  return (
    <Box
      sx={{
        height: {
          xs: 'auto',
          md: '50vh'
        },
        justifyContent: 'space-around',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          height: '20%'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: '10px',
            color: theme.palette.primary.main
          }}
        >
          {t('BUREAU_FORM.STEP_SEVEN.TITLE')}
        </Typography>
      </Box>

      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          handleChange,
          handleBlur,
          setFieldValue
        }) => {
          return (
            <Box
              onSubmit={handleSubmit}
              component="form"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '80%',
                overflow: 'auto'
              }}
            >
              <Box className={classes.container}>
                <FormLabel>
                  {t(
                    'BUREAU_FORM.STEP_SEVEN.IS_DOCTOR_WITH_TITLE_OR_CERTIFICATE'
                  )}
                </FormLabel>
                <DropdownMenu
                  list={FormOptionsType}
                  value={values.isDoctorWithTitle}
                  setValue={value => setFieldValue('isDoctorWithTitle', value)}
                  error={Boolean(
                    touched.isDoctorWithTitle && errors.isDoctorWithTitle
                  )}
                  errorText={
                    touched.isDoctorWithTitle && t(errors.isDoctorWithTitle)
                  }
                />
                <FormLabel>
                  {t('BUREAU_FORM.STEP_SEVEN.YEARS_OPERATING')}
                </FormLabel>
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(
                    touched.yearsOperating && errors.yearsOperating
                  )}
                  fullWidth
                  helperText={
                    touched.yearsOperating && t(errors.yearsOperating)
                  }
                  margin="normal"
                  name="yearsOperating"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.yearsOperating}
                  variant="outlined"
                  type="number"
                  min={1}
                />
                <Typography
                  variant="h3"
                  sx={{
                    marginTop: '25px',
                    marginBottom: '10px',
                    color: theme.palette.primary.main
                  }}
                >
                  {t('BUREAU_FORM.STEP_SEVEN.SOCIAL_MEDIA')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    marginBottom: '10px',
                    color: theme.palette.primary.main
                  }}
                >
                  {t('BUREAU_FORM.STEP_SEVEN.SOCIAL_MEDIA_DESCRIPTION')}
                </Typography>
                <FormLabel>{t('BUREAU_FORM.STEP_SEVEN.INSTAGRAM')}</FormLabel>
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.instagram && errors.instagram)}
                  fullWidth
                  helperText={touched.instagram && t(errors.instagram)}
                  margin="normal"
                  name="instagram"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.instagram}
                  variant="outlined"
                  type="text"
                />
                <FormLabel>{t('BUREAU_FORM.STEP_SEVEN.FACEBOOK')}</FormLabel>
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.facebook && errors.facebook)}
                  fullWidth
                  helperText={touched.facebook && t(errors.facebook)}
                  margin="normal"
                  name="facebook"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.facebook}
                  variant="outlined"
                  type="text"
                />
                <FormLabel>{t('BUREAU_FORM.STEP_SEVEN.TIKTOK')}</FormLabel>
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.tikTok && errors.tikTok)}
                  fullWidth
                  helperText={touched.tikTok && t(errors.tikTok)}
                  margin="normal"
                  name="tikTok"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.tikTok}
                  variant="outlined"
                  type="text"
                />
              </Box>
              <Box className={classes.container}>
                <Typography
                  variant="h3"
                  sx={{
                    marginTop: '5rem',
                    marginBottom: '10px',
                    color: theme.palette.primary.main
                  }}
                >
                  {t('BUREAU_FORM.STEP_EIGHT.TITLE')}
                </Typography>
                <FormLabel>
                  {t('BUREAU_FORM.STEP_EIGHT.MONTHLY_INCOME')}
                </FormLabel>
                <TextField
                  InputProps={{
                    autoComplete: 'off',
                    inputComponent: MoneyMask,
                  }}
        InputLabelProps={{ shrink: true }}
                  error={Boolean(touched.monthlyIncome && errors.monthlyIncome)}
                  fullWidth
                  helperText={touched.monthlyIncome && t(errors.monthlyIncome)}
                  margin="normal"
                  name="monthlyIncome"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.monthlyIncome}
                  variant="outlined"
                />
              </Box>

              <Box
                className={classes.box}
                display="flex"
                justifyContent="space-between"
              >
                <Button
                  className={classes.buttons}
                  onClick={() => handleBack()}
                  color="primary"
                  variant="outlined"
                  disabled={isLoadingCreateForm}
                >
                  {t('COMMON.BACK')}
                </Button>
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                  disabled={isLoadingCreateForm}
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};
StepFive.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func
};

export default StepFive;
